import {
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useId,
  useRef,
  useState,
} from "react";

import { Button } from "../../core/Button.js";
import { cn } from "../../foundations/cn.js";
import { FIELD_FOCUS, FIELD_WELL } from "../../foundations/interaction.js";

export interface ChatComposerProps {
  /** The field's name for screen readers: "Send a message". */
  label: string;
  placeholder?: string;
  /** The send button's words: "Chat". */
  sendLabel: string;
  /**
   * Sends the draft. Return `false` (or resolve to it) to keep the draft, e.g. when the server
   * refused it; a rejection keeps it too. Anything else clears it, unless the reader has typed
   * something new meanwhile. While it is pending the composer won't send again.
   */
  onSend: (text: string) => boolean | undefined | Promise<boolean | undefined>;
  /** Controls the draft; leave out to let the composer hold it. */
  value?: string;
  onValueChange?: (value: string) => void;
  maxLength?: number;
  /** A button inside the field that opens the emote picker. */
  emote?: ReactNode;
  /** Small buttons under the field: rules, settings. */
  tools?: ReactNode;
  /** The room's mode, beside the tools: "Slow mode · 3s". */
  mode?: string;
  /**
   * Why the reader can't chat yet (signed out, rules not agreed, muted, connecting), shown in
   * the field's place, with the one action that fixes it.
   */
  gate?: { message: ReactNode; action?: { label: string; onClick: () => void } };
  /** Sending: the button shows it and ignores presses; the field stays editable. */
  /** Why the last send was refused, in words; shown above the field until the next send. */
  error?: string;
  sending?: boolean;
  inputRef?: Ref<HTMLTextAreaElement>;
  className?: string;
}

/**
 * The bottom of a live chat: a plain field (Enter sends, Shift+Enter breaks the line), the room's
 * tools and mode, and a small send button. A stream-chat input, not a messaging app's.
 */
export function ChatComposer({
  label,
  placeholder,
  sendLabel,
  onSend,
  value,
  onValueChange,
  maxLength,
  emote,
  tools,
  mode,
  gate,
  error,
  sending = false,
  inputRef,
  className,
}: ChatComposerProps) {
  const [ownDraft, setOwnDraft] = useState("");
  const [pending, setPending] = useState(false);
  const draft = value ?? ownDraft;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const busy = sending || pending;
  const setDraft = (next: string) => {
    if (value === undefined) setOwnDraft(next);
    onValueChange?.(next);
  };
  const formRef = useRef<HTMLFormElement>(null);
  const modeId = useId();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || busy || gate) return;
    setPending(true);
    let kept = true;
    try {
      kept = (await onSend(text)) === false;
    } catch {
      // A failed send keeps the draft to retry; telling the reader why is the caller's.
    } finally {
      setPending(false);
    }
    if (!kept && draftRef.current.trim() === text) setDraft("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter breaks the line; an input method composing a character keeps it.
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      className={cn("flex flex-col gap-2 border-t border-border-subtle p-3", className)}
    >
      {error && !gate && (
        <p role="alert" className="px-1 font-body text-body-b3 text-status-danger">
          {error}
        </p>
      )}
      {gate ? (
        <div className="flex min-h-10 items-center justify-between gap-3 rounded-sm bg-bg-raised px-3 py-2 font-body text-body-b2 text-text-secondary">
          <span>{gate.message}</span>
          {gate.action && (
            <Button size="sm" className="shrink-0 rounded-sm" onClick={gate.action.onClick}>
              {gate.action.label}
            </Button>
          )}
        </div>
      ) : (
        <div
          className={cn(
            FIELD_WELL,
            FIELD_FOCUS,
            "flex items-start gap-2 rounded-sm py-2 pr-2 pl-3",
          )}
        >
          <textarea
            ref={inputRef}
            aria-label={label}
            aria-describedby={mode ? modeId : undefined}
            placeholder={placeholder}
            value={draft}
            maxLength={maxLength}
            rows={1}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            className="max-h-24 min-h-5 flex-1 resize-none bg-transparent font-body text-body-b2 leading-5 text-text-primary outline-none [field-sizing:content] placeholder:text-text-tertiary pointer-coarse:text-base pointer-coarse:leading-5"
          />
          {emote}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        {tools}
        <span
          id={modeId}
          className="min-w-0 flex-1 truncate px-1 font-mono text-mono-meta text-text-tertiary"
        >
          {mode}
        </span>
        {!gate && (
          <Button type="submit" size="sm" loading={busy} className="rounded-sm">
            {sendLabel}
          </Button>
        )}
      </div>
    </form>
  );
}
